import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import {
  Building,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  MessagesSquare,
  Package,
  Search,
  Users,
  UsersRound,
  X,
} from 'lucide-react';
import { AuthProvider, useAuth } from './auth/AuthContext.tsx';
import { Login } from './pages/Login.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Campaigns } from './pages/Campaigns.tsx';
import { Prospects } from './pages/Prospects.tsx';
import { Kbd, Spinner, ToastProvider, cx } from './ui/primitives.tsx';
import { Logo } from './ui/Logo.tsx';
import { ErrorBoundary } from './ui/ErrorBoundary.tsx';
import { CommandPalette } from './ui/CommandPalette.tsx';
import type { PaletteDestination } from './ui/CommandPalette.tsx';
import { useAiSpend, useQueueSummary, useWhatsAppStatus } from './api/queries.ts';

// As telas que não são a rotina do dia carregam sob demanda: a ficha é a maior
// do painel, e catálogo, perfis e conta raramente são abertos.
const Prospect = lazy(() => import('./pages/Prospect.tsx').then((m) => ({ default: m.Prospect })));
const Queue = lazy(() => import('./pages/Queue.tsx').then((m) => ({ default: m.Queue })));
const ProposalPage = lazy(() => import('./pages/Discovery.tsx').then((m) => ({ default: m.ProposalPage })));
const Products = lazy(() => import('./pages/Products.tsx').then((m) => ({ default: m.Products })));
const Profiles = lazy(() => import('./pages/Profiles.tsx').then((m) => ({ default: m.Profiles })));
const Account = lazy(() => import('./pages/Account.tsx').then((m) => ({ default: m.Account })));
const Team = lazy(() => import('./pages/Team.tsx').then((m) => ({ default: m.Team })));
const TeamMemberPage = lazy(() => import('./pages/Team.tsx').then((m) => ({ default: m.TeamMemberPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // O painel fica aberto o dia todo; refetch a cada foco de janela geraria
      // rajada de requisição sem novidade nenhuma. As exceções (fila, status
      // do WhatsApp, gasto de IA) ligam isso nelas mesmas (`api/queries.ts`).
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function Loading() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-[var(--color-ink-faint)]">
      <Spinner label="Carregando" />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Enquanto o /auth/me não responde não dá para saber se há sessão. Renderizar
  // o login aqui faria a tela piscar em todo reload de quem está logado.
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  return <>{children}</>;
}

// --- Navegação ----------------------------------------------------------------

interface NavEntry {
  to: string;
  label: string;
  icon: ReactNode;
  /** Ativo só nesta rota exata (o Painel não acende em toda página). */
  end?: boolean;
  /** Só o admin vê — a API também recusa o resto; isto só não oferece o que daria 403. */
  ownerOnly?: boolean;
  /** Contador ao lado do nome: o que espera alguém nesta tela. */
  badge?: 'drafts' | 'replies';
  /** Ativo quando a URL tem este `?tab=` (Abordagens e Respostas são a mesma tela). */
  tab?: string;
}

const NAV_GROUPS: { label: string; items: NavEntry[] }[] = [
  {
    label: 'Operação',
    items: [
      { to: '/', label: 'Painel', icon: <LayoutDashboard />, end: true },
      { to: '/fila', label: 'Abordagens', icon: <Inbox />, badge: 'drafts', tab: 'abordagens' },
      { to: '/fila?tab=respostas', label: 'Respostas', icon: <MessagesSquare />, badge: 'replies', tab: 'respostas' },
      { to: '/prospects', label: 'Prospects', icon: <Users /> },
      { to: '/campanhas', label: 'Campanhas', icon: <Megaphone /> },
    ],
  },
  {
    label: 'Configuração',
    items: [
      { to: '/produtos', label: 'Produtos', icon: <Package /> },
      { to: '/perfis', label: 'Perfis', icon: <Building />, ownerOnly: true },
      { to: '/equipe', label: 'Equipe', icon: <UsersRound />, ownerOnly: true },
    ],
  },
];

function isActive(entry: NavEntry, pathname: string, search: string): boolean {
  const path = entry.to.split('?')[0]!;
  if (entry.tab) {
    if (pathname !== path) return false;
    const tab = new URLSearchParams(search).get('tab') ?? 'abordagens';
    return tab === entry.tab;
  }
  return entry.end ? pathname === path : pathname === path || pathname.startsWith(`${path}/`);
}

const ITEM =
  'group relative flex h-9 items-center gap-3 rounded-[var(--radius-sm)] px-3 text-sm transition-colors [&_svg]:size-[18px] [&_svg]:shrink-0';

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user, profiles, logout, switchProfile } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const summary = useQueueSummary();
  const activeProfile = profiles.find((profile) => profile.id === user?.activeProfileId);

  return (
    <div className="flex h-full flex-col gap-7 px-4 py-6">
      <Link to="/" onClick={onNavigate} className="rounded-[var(--radius-sm)] px-2">
        <Logo size={28} product="Prospector" />
      </Link>

      <nav aria-label="Principal" className="flex flex-col gap-6">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((item) => !item.ownerOnly || user?.role === 'owner');
          if (items.length === 0) return null;
          return (
            <div key={group.label} className="flex flex-col gap-0.5">
              <p className="px-3 pb-1.5 text-[11px] font-medium text-[var(--color-ink-faint)]">{group.label}</p>
              {items.map((item) => {
                const active = isActive(item, location.pathname, location.search);
                const count = item.badge ? summary.data?.[item.badge] : undefined;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={cx(
                      ITEM,
                      active
                        ? 'bg-[var(--color-brand-soft)] font-medium text-[var(--color-ink)] shadow-[inset_0_0_0_1px_rgb(10_92_255/0.28)] before:absolute before:top-2 before:bottom-2 before:-left-4 before:w-[3px] before:rounded-full before:bg-[var(--color-cyan)] before:shadow-[0_0_12px_var(--color-cyan)]'
                        : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-hover)] hover:text-[var(--color-ink)]',
                    )}
                  >
                    <span className={active ? 'text-[var(--color-brand-text)]' : undefined}>{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    {count ? (
                      <span
                        aria-label={`${count} esperando`}
                        className={cx(
                          'tabular min-w-5 rounded-full px-1.5 text-center text-[11px] leading-5 font-semibold',
                          item.badge === 'replies'
                            ? 'bg-[var(--color-ok-soft)] text-[var(--color-ok)]'
                            : 'bg-[var(--color-brand)] text-white',
                        )}
                      >
                        {count > 99 ? '99+' : count}
                      </span>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="mt-auto flex flex-col gap-4 border-t border-[var(--color-line)] pt-5">
        {/* Com um perfil só, o nome basta; o seletor aparece quando há escolha. */}
        {profiles.length > 1 ? (
          <label className="flex flex-col gap-1.5 px-1">
            <span className="text-xs text-[var(--color-ink-faint)]">Perfil em uso</span>
            <select
              className="h-9 rounded-[var(--radius-sm)] border border-[var(--color-line-strong)] bg-[rgb(255_255_255/0.03)] px-2.5 text-sm text-[var(--color-ink)]"
              value={user?.activeProfileId ?? ''}
              onChange={(event) => {
                void switchProfile(event.target.value).then(() => {
                  // Campanhas, prospects e catálogo são todos por perfil — o
                  // cache inteiro fica do perfil anterior até isto rodar.
                  void queryClient.invalidateQueries();
                });
              }}
            >
              {profiles.map((profile) => (
                <option key={profile.id} value={profile.id} className="bg-[var(--color-raised)]">
                  {profile.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          activeProfile && (
            <div className="px-3">
              <p className="text-xs text-[var(--color-ink-faint)]">Perfil em uso</p>
              <p className="mt-0.5 truncate text-sm text-[var(--color-ink)]">{activeProfile.name}</p>
            </div>
          )
        )}

        <div className="flex flex-col gap-0.5">
          <Link
            to="/conta"
            onClick={onNavigate}
            aria-current={location.pathname === '/conta' ? 'page' : undefined}
            className={cx(
              ITEM,
              location.pathname === '/conta'
                ? 'bg-[var(--color-brand-soft)] text-[var(--color-ink)]'
                : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-hover)] hover:text-[var(--color-ink)]',
            )}
          >
            <KeyRound />
            <span className="min-w-0 truncate">{user?.name ?? 'Conta'}</span>
          </Link>
          <button
            type="button"
            onClick={() => void logout()}
            className={cx(ITEM, 'text-[var(--color-ink-soft)] hover:bg-[var(--color-hover)] hover:text-[var(--color-ink)]')}
          >
            <LogOut />
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Barra superior ------------------------------------------------------------

/** O WhatsApp está conectado? Ver isso no topo é melhor que descobrir pelo envio que falhou. */
function WhatsAppPill() {
  const { data, isError } = useWhatsAppStatus();
  if (!data && !isError) return null;
  const state = isError
    ? { tone: 'muted', label: 'WhatsApp: sem resposta' }
    : !data!.configured || !data!.enabled
      ? { tone: 'muted', label: 'WhatsApp desligado' }
      : data!.state === 'open'
        ? { tone: 'ok', label: 'WhatsApp conectado' }
        : { tone: 'warn', label: data!.error ? 'WhatsApp: sem resposta' : 'WhatsApp desconectado' };
  return (
    <span
      title={data?.error ?? (data?.instance ? `Instância ${data.instance}` : undefined)}
      className="hidden items-center gap-2 rounded-full border border-[var(--color-line)] bg-[rgb(255_255_255/0.02)] px-3 py-1 text-xs text-[var(--color-ink-soft)] md:inline-flex"
    >
      <span
        aria-hidden="true"
        className={cx(
          'size-1.5 rounded-full',
          state.tone === 'ok'
            ? 'bg-[var(--color-ok)] shadow-[0_0_8px_var(--color-ok)]'
            : state.tone === 'warn'
              ? 'bg-[var(--color-warn)] shadow-[0_0_8px_var(--color-warn)]'
              : 'bg-[var(--color-ink-faint)]',
        )}
      />
      {state.label}
    </span>
  );
}

/** O gasto de IA das últimas 24h contra o teto — o mesmo número que adia a fila quando estoura. */
function AiSpendPill() {
  const { data } = useAiSpend();
  if (!data || data.provider === 'mock') return null;
  const ratio = data.dailyBudgetUsd > 0 ? data.spent24h / data.dailyBudgetUsd : 0;
  return (
    <span
      title="Gasto de IA nas últimas 24h, somando todos os fornecedores, contra o teto diário"
      className="hidden items-center gap-2 rounded-full border border-[var(--color-line)] bg-[rgb(255_255_255/0.02)] px-3 py-1 text-xs text-[var(--color-ink-soft)] lg:inline-flex"
    >
      IA
      <span className="relative h-1 w-12 overflow-hidden rounded-full bg-[rgb(255_255_255/0.08)]">
        <span
          className={cx(
            'absolute inset-y-0 left-0 rounded-full',
            ratio >= 0.9 ? 'bg-[var(--color-danger)]' : ratio >= 0.7 ? 'bg-[var(--color-warn)]' : 'bg-[var(--color-brand-text)]',
          )}
          style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }}
        />
      </span>
      <span className="tabular">
        US$ {data.spent24h.toFixed(2).replace('.', ',')} / {data.dailyBudgetUsd.toFixed(2).replace('.', ',')}
      </span>
    </span>
  );
}

function Topbar({ onMenu, onSearch }: { onMenu: () => void; onSearch: () => void }) {
  return (
    <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-[var(--color-line)] bg-[rgb(5_6_10/0.72)] px-4 backdrop-blur-md sm:px-8">
      <button
        type="button"
        aria-label="Abrir menu"
        onClick={onMenu}
        className="-ml-1 inline-flex size-9 items-center justify-center rounded-full text-[var(--color-ink-soft)] hover:bg-[var(--color-hover)] lg:hidden"
      >
        <Menu className="size-5" aria-hidden="true" />
      </button>
      <span className="lg:hidden">
        <Logo size={22} showName={false} />
      </span>
      <button
        type="button"
        onClick={onSearch}
        className="flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-full border border-[var(--color-line)] bg-[rgb(255_255_255/0.02)] px-3.5 text-sm text-[var(--color-ink-faint)] transition-colors hover:border-[var(--color-line-strong)] sm:max-w-sm"
      >
        <Search className="size-4 shrink-0" aria-hidden="true" />
        <span className="truncate">Buscar empresa ou tela</span>
        <span className="ml-auto hidden items-center gap-1 sm:inline-flex">
          <Kbd>Ctrl</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
      <div className="ml-auto flex items-center gap-2">
        <AiSpendPill />
        <WhatsAppPill />
      </div>
    </div>
  );
}

// --- Casca ----------------------------------------------------------------------

function Shell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const drawerRef = useRef<HTMLDialogElement>(null);
  const location = useLocation();
  useEffect(() => setDrawer(false), [location.pathname, location.search]);

  // A gaveta é um <dialog> nativo: foco preso dentro dela e Esc fecha, de graça.
  useEffect(() => {
    const dialog = drawerRef.current;
    if (!dialog) return;
    if (drawer && !dialog.open) dialog.showModal();
    if (!drawer && dialog.open) dialog.close();
  }, [drawer]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPalette((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const destinations: PaletteDestination[] = NAV_GROUPS.flatMap((group) => group.items)
    .filter((item) => !item.ownerOnly || user?.role === 'owner')
    .map((item) => ({ to: item.to, label: item.label, icon: item.icon }))
    .concat([{ to: '/conta', label: 'Conta e senha', icon: <KeyRound /> }]);

  return (
    <div className="app-backdrop min-h-full lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      {/* Barra lateral fixa no desktop. */}
      <aside className="hidden border-r border-[var(--color-line)] bg-[rgb(7_8_13/0.82)] backdrop-blur-md lg:block">
        <div className="sticky top-0 h-screen overflow-y-auto">
          <Sidebar />
        </div>
      </aside>

      <dialog
        ref={drawerRef}
        id="menu-gaveta"
        aria-label="Menu"
        onClose={() => setDrawer(false)}
        onCancel={(event) => {
          event.preventDefault();
          setDrawer(false);
        }}
        onClick={(event) => {
          if (event.target === drawerRef.current) setDrawer(false);
        }}
        className="m-0 h-full max-h-none w-[min(288px,85vw)] border-0 border-r border-[var(--color-line)] bg-[var(--color-night)] p-0 text-[var(--color-ink)] lg:hidden"
      >
        {drawer && (
          <div className="relative h-full">
            <button
              type="button"
              aria-label="Fechar menu"
              onClick={() => setDrawer(false)}
              className="absolute top-5 right-3 inline-flex size-8 items-center justify-center rounded-full text-[var(--color-ink-faint)] hover:bg-[var(--color-hover)]"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
            <Sidebar onNavigate={() => setDrawer(false)} />
          </div>
        )}
      </dialog>

      <div className="flex min-w-0 flex-col">
        <Topbar onMenu={() => setDrawer(true)} onSearch={() => setPalette(true)} />
        <main className="mx-auto w-full max-w-[1280px] min-w-0 px-4 pt-7 pb-16 sm:px-8 lg:pt-9">
          <ErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<Loading />}>{children}</Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <CommandPalette open={palette} onClose={() => setPalette(false)} destinations={destinations} />
    </div>
  );
}

/** Tela só do admin (vai dentro de `Page`, que já esperou o /auth/me): quem não é volta ao painel em vez de ver um 403 girando. */
function OwnerOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return user?.role === 'owner' ? <>{children}</> : <Navigate to="/" replace />;
}

function Page({ children, bare = false }: { children: ReactNode; bare?: boolean }) {
  return (
    <RequireAuth>
      {bare ? (
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>{children}</Suspense>
        </ErrorBoundary>
      ) : (
        <Shell>{children}</Shell>
      )}
    </RequireAuth>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Page><Dashboard /></Page>} />
              <Route path="/fila" element={<Page><Queue /></Page>} />
              <Route path="/prospects" element={<Page><Prospects /></Page>} />
              <Route path="/prospects/:id" element={<Page><Prospect /></Page>} />
              {/* Sem o Shell: a proposta é impressa e enviada ao dono. */}
              <Route path="/prospects/:id/proposta" element={<Page bare><ProposalPage /></Page>} />
              <Route path="/campanhas" element={<Page><Campaigns /></Page>} />
              <Route path="/produtos" element={<Page><Products /></Page>} />
              <Route path="/perfis" element={<Page><OwnerOnly><Profiles /></OwnerOnly></Page>} />
              <Route path="/conta" element={<Page><Account /></Page>} />
              <Route path="/equipe" element={<Page><OwnerOnly><Team /></OwnerOnly></Page>} />
              <Route path="/equipe/:id" element={<Page><OwnerOnly><TeamMemberPage /></OwnerOnly></Page>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
